import React from 'react';
import { ImageDown, Plus, Trash2, UploadCloud } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select } from './ui/select';

const CreateCardForm = ({
  contactInfo,
  handleContactInfoChange,
  customFields,
  handleCustomFieldChange,
  handleAddCustomField,
  handleRemoveCustomField,
  getRootPropsProfile,
  getInputPropsProfile,
  profilePic,
  profilePreview,
  getRootPropsCover,
  getInputPropsCover,
  coverPic,
  coverPreview,
  selectedCover,
  handleCoverSelectChange,
  theme,
  setTheme,
  handleSubmit,
  isSubmitting,
  availableCovers
}) => {
  return (
    <form onSubmit={handleSubmit} className="space-y-10">
      <section className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Informazioni principali</h3>
          <p className="text-sm text-gray-500">
            Questi dati appariranno nella parte superiore del tuo biglietto digitale.
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input id="name" name="name" value={contactInfo.name} onChange={handleContactInfoChange} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="surname">Cognome</Label>
            <Input id="surname" name="surname" value={contactInfo.surname} onChange={handleContactInfoChange} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="organization">Organizzazione</Label>
            <Input
              id="organization"
              name="organization"
              value={contactInfo.organization}
              onChange={handleContactInfoChange}
              placeholder="Es. E-Card s.r.l."
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="jobTitle">Ruolo</Label>
            <Input
              id="jobTitle"
              name="jobTitle"
              value={contactInfo.jobTitle}
              onChange={handleContactInfoChange}
              placeholder="Es. Marketing Manager"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" name="email" value={contactInfo.email} onChange={handleContactInfoChange} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Telefono</Label>
            <Input id="phone" name="phone" value={contactInfo.phone} onChange={handleContactInfoChange} placeholder="Es. +39 333 123 4567" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="address">Indirizzo</Label>
            <Input id="address" name="address" value={contactInfo.address} onChange={handleContactInfoChange} placeholder="Inserisci indirizzo o sede" />
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Immagini</h3>
          <p className="text-sm text-gray-500">
            Carica un ritratto e un’immagine di copertina per rendere unico il tuo biglietto.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-3">
            <Label>Foto profilo</Label>
            <div
              {...getRootPropsProfile({ className: 'dropzone' })}
              className="group relative flex h-40 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 text-center transition hover:border-blue-400 hover:bg-blue-50"
            >
              <input {...getInputPropsProfile()} />
              {profilePreview ? (
                <img src={profilePreview} alt="Anteprima profilo" className="h-full w-full rounded-lg object-cover" />
              ) : (
                <div className="flex flex-col items-center space-y-2 text-sm text-gray-500">
                  <UploadCloud className="h-6 w-6 text-gray-400" />
                  <span className="font-medium text-gray-600">Trascina o clicca per caricare</span>
                  <span>JPEG o PNG, max 5MB</span>
                </div>
              )}
            </div>
            {profilePic && !profilePreview && (
              <p className="text-xs text-gray-500">File selezionato: {profilePic.name}</p>
            )}
          </div>

          <div className="space-y-3">
            <Label>Copertina</Label>
            <div
              {...getRootPropsCover({ className: 'dropzone' })}
              className="group relative flex h-40 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 text-center transition hover:border-blue-400 hover:bg-blue-50"
            >
              <input {...getInputPropsCover()} />
              {coverPreview && !selectedCover ? (
                <img src={coverPreview} alt="Anteprima copertina" className="h-full w-full rounded-lg object-cover" />
              ) : (
                <div className="flex flex-col items-center space-y-2 text-sm text-gray-500">
                  <ImageDown className="h-6 w-6 text-gray-400" />
                  <span className="font-medium text-gray-600">Carica un’immagine personalizzata</span>
                  <span>Consigliato 1200x600px</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="coverSelect" className="text-sm font-medium">
                Oppure scegli una copertina pronta
              </Label>
              <Select id="coverSelect" value={selectedCover} onChange={handleCoverSelectChange}>
                <option value="">Nessuna copertina</option>
                {availableCovers.map((cover) => (
                  <option key={cover.name} value={cover.src}>
                    {cover.name}
                  </option>
                ))}
              </Select>
              {selectedCover && (
                <img src={selectedCover} alt="Copertina scelta" className="h-24 w-full rounded-md object-cover" />
              )}
              {coverPic && !coverPreview && typeof coverPic !== 'string' && (
                <p className="text-xs text-gray-500">File selezionato: {coverPic.name}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Tema grafico</h3>
          <p className="text-sm text-gray-500">Scegli lo stile visivo del biglietto digitale.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {[
            {
              value: 'bilda2025',
              title: 'Bilda 2025',
              description: 'Stile moderno con gradienti e accenti dinamici.'
            },
            {
              value: 'classic',
              title: 'Classic',
              description: 'Layout tradizionale, elegante e pulito.'
            },
            {
              value: 'minimal',
              title: 'Minimal',
              description: 'Design chiaro, tipografia pulita e sezioni essenziali.'
            },
            {
              value: 'aurora',
              title: 'Aurora',
              description: 'Tema scuro con effetti glassmorphism e gradienti morbidi.'
            }
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setTheme(option.value)}
              className={`rounded-lg border p-4 text-left transition hover:border-blue-400 hover:shadow ${
                theme === option.value ? 'border-blue-500 shadow-md' : 'border-gray-200'
              }`}
            >
              <p className="text-sm font-semibold text-gray-900">{option.title}</p>
              <p className="mt-1 text-xs text-gray-500">{option.description}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Campi personalizzati</h3>
            <p className="text-sm text-gray-500">Inserisci link o informazioni extra (es. social, siti web, calendly).</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={handleAddCustomField} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Aggiungi campo
          </Button>
        </div>

        <div className="space-y-4">
          {customFields.map((field, index) => (
            <div key={`${index}-${field.label}`} className="grid gap-3 rounded-lg border border-gray-200 p-4 md:grid-cols-[1fr_1fr_auto]">
              <div className="space-y-2">
                <Label htmlFor={`custom-label-${index}`}>Etichetta</Label>
                <Input
                  id={`custom-label-${index}`}
                  name="label"
                  placeholder="Es. LinkedIn"
                  value={field.label}
                  onChange={(event) => handleCustomFieldChange(index, event)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`custom-value-${index}`}>Valore</Label>
                <Input
                  id={`custom-value-${index}`}
                  name="value"
                  placeholder="Es. https://linkedin.com/in/..."
                  value={field.value}
                  onChange={(event) => handleCustomFieldChange(index, event)}
                />
              </div>
              <div className="flex items-end justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveCustomField(index)}
                  className="text-red-500 hover:text-red-600"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Rimuovi
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="flex justify-end border-t border-gray-200 pt-6">
        <Button type="submit" size="lg" disabled={isSubmitting}>
          {isSubmitting ? 'Creazione in corso…' : 'Crea biglietto' }
        </Button>
      </div>
    </form>
  );
};

export default CreateCardForm;
